import React, { useState, useEffect, useCallback } from 'react';
import { FaEye, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Breadcrumbs from '../components/Breadcrumbs';
import '../assets/css/Gallery.css';
import { API_BASE_URL, UPLOADS_BASE_URL } from '../config';

const Gallery = () => {
  const [galleries, setGalleries] = useState([]);
  const [activeGallery, setActiveGallery] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      console.log('Fetching galleries from API...');
      
      const response = await fetch(`${API_BASE_URL}gallery.php?include_images=true`);
      const data = await response.json();
      
      console.log('Gallery API response:', data);
      
      if (data && data.success && data.galleries && Array.isArray(data.galleries)) {
        // Filter galleries that have images
        const galleriesWithImages = data.galleries.filter(gallery => 
          gallery.image_count > 0 && gallery.images && gallery.images.length > 0
        );
        
        console.log('Galleries with images:', galleriesWithImages);
        
        // Sort galleries by ID (1, 2, 3...)
        const sortedGalleries = galleriesWithImages.sort((a, b) => parseInt(a.id) - parseInt(b.id));
        
        setGalleries(sortedGalleries);
        
        // Set first gallery as active by default
        if (sortedGalleries.length > 0) {
          const firstGallery = sortedGalleries[0];
          setActiveGallery(firstGallery);
          // Sort images by created_at in descending order (most recent first)
          const sortedImages = (firstGallery.images || []).sort((a, b) => {
            // Handle cases where created_at might be missing
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            return dateB - dateA; // Descending order (newest first)
          });
          setAllImages(sortedImages);
        }
      } else {
        console.error('Invalid API response structure:', data);
      }
    } catch (error) {
      console.error('Error fetching galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryChange = (gallery) => {
    setActiveGallery(gallery);
    // Sort images by created_at in descending order (most recent first)
    const sortedImages = (gallery.images || []).sort((a, b) => {
      // Handle cases where created_at might be missing
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateB - dateA; // Descending order (newest first)
    });
    setAllImages(sortedImages);
    setSelectedImage(null);
    setSelectedIndex(0);
    console.log(`Switched to gallery: ${gallery.name} (${gallery.images?.length || 0} images)`);
  };

  const openLightbox = useCallback((image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    setSelectedIndex(0);
    document.body.style.overflow = 'auto';
  }, []);

  const goToPrevious = useCallback(() => {
    if (allImages.length > 1) {
      const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : allImages.length - 1;
      setSelectedIndex(prevIndex);
      setSelectedImage(allImages[prevIndex]);
    }
  }, [selectedIndex, allImages]);

  const goToNext = useCallback(() => {
    if (allImages.length > 1) {
      const nextIndex = selectedIndex < allImages.length - 1 ? selectedIndex + 1 : 0;
      setSelectedIndex(nextIndex);
      setSelectedImage(allImages[nextIndex]);
    }
  }, [selectedIndex, allImages]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, closeLightbox, goToPrevious, goToNext]);

  const breadcrumbsConfig = {
    title: "Gallery",
    links: [
      { text: "Home", url: "/" },
      { text: "Gallery", url: "/gallery" }
    ]
  };

  if (loading) {
    return (
      <main className="main-content">
        <Breadcrumbs {...breadcrumbsConfig} />
        <div className="gallery-section">
          <div className="loading-container" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px',
            fontSize: '18px',
            color: '#666'
          }}>
            Loading galleries...
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content">
      <Breadcrumbs {...breadcrumbsConfig} />

      <div className="gallery-section">
        <div className="gallery-header">
          <h1>Gallery</h1>
          <p>Explore our latest work</p>
        </div>

        {/* Gallery Tabs */}
        {galleries.length > 0 && (
          <div className="gallery-tabs">
            {galleries.map((gallery) => (
              <button
                key={gallery.id}
                className={`gallery-tab ${activeGallery?.id === gallery.id ? 'active' : ''}`}
                onClick={() => handleGalleryChange(gallery)}
              >
                <span className="tab-name">{gallery.name}</span>
                {/* <span className="tab-count">({gallery.image_count})</span> */}
              </button>
            ))}
          </div>
        )}

        {/* Active Gallery Info */}
        {/* {activeGallery && (
          <div className="active-gallery-info">
            <h2>{activeGallery.name}</h2>
            {activeGallery.description && activeGallery.description !== activeGallery.name && (
              <p>{activeGallery.description}</p>
            )}
            <div className="gallery-stats">
              <span>{activeGallery.image_count} {activeGallery.image_count === 1 ? 'image' : 'images'}</span>
            </div>
          </div>
        )} */}

        {/* Gallery Grid */}
        <div className="gallery-container">
          {allImages.length > 0 ? (
            <div className="gallery-grid">
              {allImages.map((image, index) => (
                <div 
                  key={image.id} 
                  className="gallery-item"
                  onClick={() => openLightbox(image, index)}
                >
                  <div className="gallery-item-inner">
                    <img 
                      src={image.image_url}
                      alt={`${activeGallery?.name} - Image ${index + 1}`}
                      loading="lazy"
                      onError={(e) => {
                        console.error('Image failed to load:', image.image_url);
                        e.target.src = 'https://via.placeholder.com/400x300/f0f0f0/666?text=Image+Not+Available';
                      }}
                    />
                    <div className="gallery-item-overlay">
                      <div className="gallery-item-content">
                        <div className="view-icon">
                          <FaEye size={24} />
                        </div>
                        {/* <div className="image-info">
                          <span className="image-number">#{image.sort_order + 1}</span>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-images">
              <p>No images available in this gallery.</p>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Lightbox */}
      {selectedImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <button 
            className="lightbox-close" 
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <FaTimes />
          </button>
          
          {allImages.length > 1 && (
            <>
              <button 
                className="lightbox-nav lightbox-prev" 
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>
              
              <button 
                className="lightbox-nav lightbox-next" 
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>
            </>
          )}
          
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedImage.image_url}
              alt={`${activeGallery?.name} - Image ${selectedIndex + 1}`}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x600/f0f0f0/666?text=Image+Not+Found';
              }}
            />
            {/* <div className="lightbox-details">
              <h3>{activeGallery?.name}</h3>
              <p className="lightbox-counter">
                Image {selectedIndex + 1} of {allImages.length}
              </p>
              {selectedImage.created_at && (
                <p className="image-date">
                  Uploaded: {new Date(selectedImage.created_at).toLocaleDateString()}
                </p>
              )}
            </div> */}
          </div>
        </div>
      )}
    </main>
  );
};

export default Gallery; 